/**
 * Custom hook for fetching the current user's full team hierarchy.
 *
 * ⚠️ NAME MATTERS HERE. This was `useDirectReportIds` in `useDirectReports.ts`,
 * which was wrong in a way that mattered: `GET /users/me/direct-report-ids` is
 * served by the RECURSIVE `getTeamHierarchy`, so it returns every subordinate
 * at every level, not just direct reports. Item B scopes reports to
 * `self + DIRECT reports`; a reader who trusted the old name would have wired
 * the wrong set into a scoping decision.
 *
 * Reports do not use this hook at all — they read `scope.ownerCount` off the
 * API response. Its one consumer is OKR list filtering (`useOKRFilters`).
 * The endpoint's behaviour is unchanged: renaming the hook must not alter what
 * the OKR list shows.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services';

/**
 * Hook to fetch IDs of all users in the current user's team hierarchy
 * (recursive — every level, not just direct reports).
 * Only enabled for managers - other roles get empty array immediately
 */
export const useTeamHierarchyIds = () => {
  const { user } = useAuth();

  return useQuery<string[]>({
    queryKey: ['direct-report-ids'],
    queryFn: () => userService.getMyDirectReportIds(),
    // Only fetch for managers (account_admin can already see all OKRs)
    enabled: user?.role === 'manager',
    // Cache for longer since team structure changes infrequently
    staleTime: 1000 * 60 * 15, // 15 minutes
    // Return empty array for non-managers without making API call
    placeholderData: [],
  });
};
